// Subscription tier definitions
export type SubscriptionTier = "free" | "plus" | "pro"

export type SubscriptionStatus = "active" | "pending" | "expired" | "cancelled"

export type PaymentProvider = "vipps" | "apple" | "google"

// Vipps agreement status from their API
export type VippsAgreementStatus = "PENDING" | "ACTIVE" | "STOPPED" | "EXPIRED"

// Subscription stored in Firestore
export interface Subscription {
  tier: SubscriptionTier
  status: SubscriptionStatus
  provider: PaymentProvider
  // Vipps-specific fields
  vippsAgreementId?: string
  vippsCustomerPhoneNumber?: string
  // Billing cycle
  currentPeriodStart: string // ISO date
  currentPeriodEnd: string // ISO date
  cancelAtPeriodEnd: boolean
  // Pricing (stored for reference)
  priceNOK: number
  // Timestamps
  createdAt: string
  updatedAt: string
}

// Pricing configuration
export interface SubscriptionPlan {
  tier: SubscriptionTier
  name: string
  priceNOK: number
  interval: "MONTHLY"
  features: string[]
}

// Feature flags per tier
export interface TierFeatures {
  maxAccounts: number
  maxTransactionsPerMonth: number
  householdMembers: number
  // --- AI capabilities (AI-first) ---
  // Spending analysis, smart recommendations and forecasts on the Insights tab.
  aiInsights: boolean
  // The conversational financial assistant (chat that can answer questions and
  // take actions on your budget).
  aiAssistant: boolean
  // Photo / receipt -> expense scanning.
  aiReceiptScanning: boolean
  // Monthly budget of billable AI requests shared across AI features.
  // -1 = unlimited. Enforced server-side; surfaced client-side for upsell.
  aiMonthlyQuota: number
  pdfExport: boolean
  csvExport: boolean
  recurringTransactions: boolean
  savingsGoals: boolean
  investments: boolean
  // Open Banking: link a bank and auto-import transactions/balances. A paid
  // feature — free users budget manually. Enforced server-side (bank routes +
  // the nightly bank-sync cron); surfaced client-side for the paywall/upsell.
  bankSync: boolean
  prioritySupport: boolean
}

// Subscription plans configuration
export const SUBSCRIPTION_PLANS: Record<SubscriptionTier, SubscriptionPlan> = {
  free: {
    tier: "free",
    name: "Gratis",
    priceNOK: 0,
    interval: "MONTHLY",
    features: ["1 konto", "50 transaksjoner/måned", "AI-assistent (25 spørringer/mnd)", "Grunnleggende AI-innsikter"],
  },
  plus: {
    tier: "plus",
    name: "Plus",
    priceNOK: 4900, // 49 NOK in øre
    interval: "MONTHLY",
    features: [
      "AI-assistent (250 spørringer/mnd)",
      "AI-kvitteringsskanning",
      "Automatisk bankimport (Open Banking)",
      "Ubegrenset kontoer",
      "Ubegrenset transaksjoner",
      "PDF & CSV eksport",
      "Husholdning (2 medlemmer)",
      "Sparemål",
      "Gjentakende transaksjoner",
    ],
  },
  pro: {
    tier: "pro",
    name: "Pro",
    priceNOK: 14900, // 149 NOK in øre
    interval: "MONTHLY",
    features: [
      "Alt i Plus",
      "Ubegrenset AI-assistent",
      "Avanserte AI-innsikter & prognoser",
      "Ubegrenset husholdning",
      "Investeringssporing",
      "Prioritert support",
    ],
  },
}

// Feature limits per tier.
//
// AI-first model: every tier gets a real taste of the AI (assistant + insights),
// gated by a monthly request quota rather than locked away entirely. Paid tiers
// raise the quota and unlock the heavier capabilities (receipt scanning, etc.).
export const TIER_FEATURES: Record<SubscriptionTier, TierFeatures> = {
  free: {
    maxAccounts: 1,
    maxTransactionsPerMonth: 50,
    householdMembers: 0,
    aiInsights: true,
    aiAssistant: true,
    aiReceiptScanning: false,
    aiMonthlyQuota: 25,
    pdfExport: false,
    csvExport: false,
    // Free since the bank-driven onboarding shipped. Detected subscriptions are
    // one of the first things we can tell a brand-new user about their own
    // spending — and a brand-new user is on free by definition, so gating it put
    // the paywall exactly where the product needed to make its case.
    recurringTransactions: true,
    savingsGoals: false,
    investments: false,
    bankSync: false,
    prioritySupport: false,
  },
  plus: {
    maxAccounts: -1, // unlimited
    maxTransactionsPerMonth: -1, // unlimited
    householdMembers: 2,
    aiInsights: true,
    aiAssistant: true,
    aiReceiptScanning: true,
    aiMonthlyQuota: 250,
    pdfExport: true,
    csvExport: true,
    recurringTransactions: true,
    savingsGoals: true,
    investments: false,
    bankSync: true,
    prioritySupport: false,
  },
  pro: {
    maxAccounts: -1,
    maxTransactionsPerMonth: -1,
    householdMembers: -1, // unlimited
    aiInsights: true,
    aiAssistant: true,
    aiReceiptScanning: true,
    aiMonthlyQuota: -1, // unlimited
    pdfExport: true,
    csvExport: true,
    recurringTransactions: true,
    savingsGoals: true,
    investments: true,
    bankSync: true,
    prioritySupport: true,
  },
}

// Vipps API types
export interface VippsAgreement {
  agreementId: string
  status: VippsAgreementStatus
  productName: string
  price: number
  currency: "NOK"
  interval: {
    unit: "MONTH"
    count: number
  }
  campaign?: {
    type: "PRICE_CAMPAIGN"
    price: number
    period: {
      unit: "MONTH"
      count: number
    }
  }
}

export interface VippsCharge {
  chargeId: string
  status: "PENDING" | "DUE" | "CHARGED" | "FAILED" | "REFUNDED" | "CANCELLED"
  amount: number
  description: string
  dueDate: string
}

// Webhook event types from Vipps
export interface VippsWebhookEvent {
  name: string
  data: {
    agreementId?: string
    chargeId?: string
    amount?: number
    actor?: "USER" | "MERCHANT"
    msn?: string
  }
  timestamp: string
}
